import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import * as identityStore from '@core/identity';
import * as coreStore from '@core/store';
import { selectIsLoaded } from '@core/utility';
import { select, Store } from '@ngrx/store';
import { filter, map, mergeMap } from 'rxjs';
import { b2cPolicies } from '../../../../auth-config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private store: Store
  ) {
    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .subscribe(() => {
        this.updateAccountInfo();
      });

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .subscribe(() => {
        this.updateAccountInfo();
      });
  }

  public logout() {
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: '',
    });
  }

  public login() {
    this.msalService.loginRedirect().subscribe(() => {});
  }

  private updateAccountInfo() {
    this.store.dispatch(identityStore.IdentityActions.fetchIdentity());
  }

  public checkRoles(roles: string[]) {
    return this.getAccount().pipe(
      map(accountInfo => {
        if (!accountInfo || !accountInfo.idTokenClaims) {
          return false;
        }

        const accountRoles = accountInfo.idTokenClaims.roles as string[];
        return roles.some(role => accountRoles.includes(role));
      })
    );
  }

  public resetPassword() {
    const editProfileFlowRequest: RedirectRequest = {
      scopes: ['openid'],
      authority: b2cPolicies.authorities.editProfile.authority,
    };
    this.msalService.loginRedirect(editProfileFlowRequest);
  }

  public getRoles() {
    return this.store.pipe(
      select(identityStore.selectIdentityAccount(coreStore.selectIdentityState)),
      map(accountInfo => accountInfo?.idTokenClaims?.roles ?? [])
    );
  }

  public getAccount() {
    return this.identityIsLoaded().pipe(
      mergeMap(() =>
        this.store.pipe(select(identityStore.selectIdentityAccount(coreStore.selectIdentityState)))
      )
    );
  }

  public getContractorName() {
    return this.identityIsLoaded().pipe(
      mergeMap(() =>
        this.store.pipe(
          select(identityStore.selectIdentityGeneralContractorName(coreStore.selectIdentityState))
        )
      )
    );
  }

  public getContractorNumber() {
    return this.identityIsLoaded().pipe(
      mergeMap(() =>
        this.store.pipe(
          select(identityStore.selectIdentityGeneralContractorId(coreStore.selectIdentityState))
        )
      )
    );
  }

  public getUserName() {
    return this.getAccount().pipe(
      map(
        accountInfo =>
          `${accountInfo?.idTokenClaims?.given_name} ${accountInfo?.idTokenClaims?.family_name}`
      )
    );
  }

  public getLockedStatus() {
    return this.getAccount().pipe(
      map(
        accountInfo => (accountInfo?.idTokenClaims?.extension_GuPortalIsLockedValue as number) ?? 0
      )
    );
  }

  private identityIsLoaded() {
    return this.store.pipe(
      select(selectIsLoaded(coreStore.selectIdentityState)),
      filter((isLoaded: boolean) => isLoaded)
    );
  }
}
